import { useEffect, useState } from "react";
import Table from "../../../components/Table";
import MaterialTypography from "../../../components/MaterialTypography";
import { Card, CardHeader, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MaterialButton from "../../../components/MaterialButton";
import AddIcon from "@mui/icons-material/Add";
import { FileUpload, FilterAlt } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setRowsPerPage,
  setTotalPages,
} from "./store/motorModelStore";
import { deleteData, fetchData } from "../../../services";
import { useLoading } from "../../../components/Loading";
import BulkUpload from "../../../components/blukUplod";
import { useSnackbar } from "../../../context/popup";
import MotorModelListFilter from "./components/MotorModelListFilter.js";

const MotorModel = ({tabState}) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setCircleLoading } = useLoading();
  const { showSnackbar } = useSnackbar();

  const [search, setSearch] = useState("");
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [modelData, setModelData] = useState([]);
  const [isRefresh, setIsRefresh] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [filterData, setFilterData] = useState({
    isActive: "",
    motorCategoryId: "",
    motorMakeId: ""
  });

  const currentPage = useSelector(
    (state) => state?.motorModelStore?.currentPage
  );
  const rowsPerPage = useSelector(
    (state) => state?.motorModelStore?.rowsPerPage
  );
  const totalPages = useSelector((state) => state?.motorModelStore?.totalPages);

  useEffect(() => {
    if(tabState === "model"){
      fetchModelData();
    }
  }, [isRefresh, currentPage, rowsPerPage, search]);

  const fetchModelData = async () => {
    setCircleLoading(true);
    try {
      let url = `/reference/motor/mmv/models?page=${currentPage}&limit=${rowsPerPage}&searchTerm=${search}&motorCategoryId=${filterData?.motorCategoryId}&motorMakeId=${filterData?.motorMakeId}`;
      
      //for active and inactive status
      if (filterData.isActive !== "") {
        url += `&isActive=${filterData.isActive}`;
      }
      const response = await fetchData(url);
      const successResponse = response?.success;
      if (successResponse) {
        setModelData(response?.data?.items);
        dispatch(setTotalPages(response?.data?.totalPages));
      } else {
        showSnackbar("Failed to fetch Model data", "error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showSnackbar(error?.response?.data?.message || 'Network Error', "error");
    } finally {
      setCircleLoading(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      const response = await deleteData(`/reference/motor/mmv/model-delete`, {
        ids: [row?.id],
      });
      setIsRefresh(!isRefresh);
      const responseMessage = response?.message;
      if (response?.success) {
        showSnackbar(responseMessage, "success");
      } else {
        showSnackbar(responseMessage, "error");
      }
    } catch (error) {
      console.error("Error deleting data:", error);
      showSnackbar("Failed to delete Motor Model", "error");
    }
  };

  const columns = [
    {
      key: "sno",
      label: "S.No.",
      renderCell: (row) => row.sno,
    },
    {
      key: "name",
      label: "Model Name",
      renderCell: (row) => row.name,
    },
    {
      key: "motorCategoryId",
      label: "Vehicle Category",
      renderCell: (row) => row.motorCategory?.keyName,
    },
    {
      key: "motorMakeId",
      label: "Motor Make",
      renderCell: (row) => row.make?.name,
    },
    {
      key: "createdAt",
      label: "Created At",
      renderCell: (row) => new Date(row.createdAt).toLocaleDateString("en-GB"),
    },
    {
      key: "isActive",
      label: "Status",
      renderCell: (row) => (
        <span>
          {row.isActive ? (
            <span style={{ color: "green", fontWeight: "bold" }}>Active</span>
          ) : (
            <span style={{ color: "red", fontWeight: "bold" }}>Inactive</span>
          )}
        </span>
      ),
    },
  ];

  const rows = modelData?.map((model, index) => ({
    ...model,
    sno: index + 1,
  }));

  const handlePaginationChange = (page) => {
    dispatch(setCurrentPage(page));
  };

  const handleRowsPerPageChange = (rowsPerPage) => {
    dispatch(setRowsPerPage(rowsPerPage));
    dispatch(setCurrentPage(1));
  };

  const handleEdit = (row) => {
    navigate(`/motor-master/edit-model/${row?.id}`);
  };

  const handleView = (row) => {
    navigate(`/motor-master/view-model/${row?.id}`, {
      state: { isView: true },
    });
  };

  const actions = {
    edit: true,
    view: true,
    delete: true,
    onEdit: (row) => handleEdit(row),
    onView: (row) => handleView(row),
    onDelete: (row) => handleDelete(row),
  };

  const handleAddNewModel = () => {
    navigate("/motor-master/add-model");
  };

  const handleSearch = (value) => {
    setSearch(value);
  };

  return (
    <div>
      {/* <DashboardLayout> */}
      <CardHeader
        title={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Stack direction="column" spacing={0}>
              <MaterialTypography variant="h5" fontWeight="medium">
                Motor Model management list
              </MaterialTypography>
              <MaterialTypography variant="caption" color="text">
                List of motor model that you can get details of motor model.
              </MaterialTypography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FilterAlt />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
              >
                Filter
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<FileUpload />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                Bulk Upload
              </MaterialButton>
              <MaterialButton
                variant="gradient"
                color="dark"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleAddNewModel()}
              >
                Add Model
              </MaterialButton>
            </Stack>
          </Stack>
        }
      />
      <Card sx={{ mx: 1 }}>
        <Table
          columns={columns}
          data={rows}
          actions={actions}
          pagination={{
            totalPages,
            currentPage,
            onPageChange: (page) => handlePaginationChange(page),
            rowsPerPage,
            onRowsPerPageChange: (rowsPerPage) =>
              handleRowsPerPageChange(rowsPerPage),
            rowsPerPageOptions: [5, 10, 15, 20],
          }}
          getRowKey={(row) => row.id}
          //   profile={true}
          //   teamAvatar={true}
          className="custom-table"
          noDataLabel={"No motor model data found"}
          handleSearch={handleSearch}
        />
      </Card>

      <BulkUpload
        open={openUploadFileDialog}
        setOpen={setOpenUploadFileDialog}
        endPoint="/reference/master/bulkUpload/motor-model"
        fetchList={fetchModelData}
      />
      <MotorModelListFilter
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        filterData={filterData}
        setFilterData={setFilterData}
        onApply={() => {
          setAnchorEl(null); // close popup
          fetchModelData(); // call API after filter applied
        }}
      />
      {/* </DashboardLayout> */}
    </div>
  );
};

export default MotorModel;
